import { SystemData, PlacedBlock, SocketConnection } from '@/app/types/cad';
import {
  TableConfig,
  TableColumn,
  TableRow,
  TableCell,
  CellStyle,
  SystemTableData,
  HeaderCell,
} from '@/app/types/table';

// Default cell styles
export const defaultCellStyle: CellStyle = {
  backgroundColor: '#ffffff',
  textColor: '#000000',
  fontWeight: 'normal',
  fontSize: 12,
  textAlign: 'left',
  verticalAlign: 'middle',
  padding: 8,
  borders: {
    top: { visible: true, width: 1, color: '#e0e0e0', style: 'solid' },
    right: { visible: true, width: 1, color: '#e0e0e0', style: 'solid' },
    bottom: { visible: true, width: 1, color: '#e0e0e0', style: 'solid' },
    left: { visible: true, width: 1, color: '#e0e0e0', style: 'solid' },
  },
};

export const headerCellStyle: CellStyle = {
  ...defaultCellStyle,
  backgroundColor: '#f5f5f5',
  fontWeight: 'bold',
  textAlign: 'center',
  borders: {
    top: { visible: true, width: 2, color: '#000000', style: 'solid' },
    right: { visible: true, width: 1, color: '#000000', style: 'solid' },
    bottom: { visible: true, width: 2, color: '#000000', style: 'solid' },
    left: { visible: true, width: 1, color: '#000000', style: 'solid' },
  },
};

// Create empty table configuration
export function createEmptyTableConfig(): TableConfig {
  const columns: TableColumn[] = [];
  const rows: TableRow[] = [];

  // Create empty header row
  const headerRow: TableRow = {
    id: 'row-header-0',
    index: 0,
    height: 50,
    isHeader: true,
    data: {},
  };

  rows.push(headerRow);

  return {
    id: 'table-empty',
    name: 'Neue Tabelle',
    columns,
    rows,
    sections: [],
    filters: [],
    sorts: [],
    frozenColumns: 0,
    frozenRows: 1,
    showGridLines: true,
    defaultCellStyle,
  };
}

// Collect all attributes from blocks and connections
export function collectAllAttributes(
  blocks: PlacedBlock[],
  connections: SocketConnection[]
): Map<string, { count: number; type: string; objectType: 'block' | 'connection' | 'both' }> {
  const attributeMap = new Map<string, { count: number; type: string; objectType: 'block' | 'connection' | 'both' }>();

  // Collect from blocks
  if (blocks && Array.isArray(blocks)) {
    // Add "Name" as a special attribute for all blocks
    attributeMap.set('Name', {
      count: blocks.length,
      type: 'string',
      objectType: 'block',
    });

    blocks.forEach(block => {
      block.blockData.attributes?.forEach(attr => {
        const existing = attributeMap.get(attr.name);
        if (existing) {
          attributeMap.set(attr.name, {
            count: existing.count + 1,
            type: attr.type,
            objectType: existing.objectType === 'connection' ? 'both' : 'block',
          });
        } else {
          attributeMap.set(attr.name, {
            count: 1,
            type: attr.type,
            objectType: 'block',
          });
        }
      });
    });
  }

  // Collect from connections
  if (connections && Array.isArray(connections)) {
    connections.forEach(conn => {
      conn.attributes?.forEach(attr => {
        const existing = attributeMap.get(attr.name);
        if (existing) {
          attributeMap.set(attr.name, {
            count: existing.count + 1,
            type: attr.type,
            objectType: existing.objectType === 'block' ? 'both' : 'connection',
          });
        } else {
          attributeMap.set(attr.name, {
            count: 1,
            type: attr.type,
            objectType: 'connection',
          });
        }
      });
    });
  }

  return attributeMap;
}

// Add column with attribute
export function addAttributeColumn(
  config: TableConfig,
  attributeName: string,
  blocks: PlacedBlock[],
  connections: SocketConnection[]
): TableConfig {
  const newConfig = { ...config };
  const columnIndex = newConfig.columns.length;

  // Create new column
  const newColumn: TableColumn = {
    id: `col-${Date.now()}-${Math.random()}`,
    index: columnIndex,
    label: attributeName,
    dataType: 'string',
    width: 150,
    attributeKey: attributeName,
    isVisible: true,
  };

  newConfig.columns.push(newColumn);

  // If header rows exist, add empty cells for the new column in each header row
  if (newConfig.headerRows && newConfig.headerRows.length > 0) {
    console.log('📋 Adding header cells for new column in existing header rows');
    
    for (const headerRow of newConfig.headerRows) {
      // Create a new empty header cell for this column
      const newHeaderCell: HeaderCell = {
        id: `header-cell-${Date.now()}-${Math.random()}`,
        label: '', // Empty by default, user can edit later
        colspan: 1,
        rowspan: 1,
        columnStartIndex: columnIndex,
      };
      
      headerRow.cells.push(newHeaderCell);
      console.log(`  ✅ Added header cell to row ${headerRow.id}`);
    }
  }

  // If this is the first column, collect all objects with this attribute
  if (newConfig.columns.length === 1) {
    // Collect all objects that have this attribute
    const objectsWithAttribute: Array<{
      id: string;
      name: string;
      type: 'block' | 'connection';
      attributeValue: any;
      allAttributes: Map<string, any>;
    }> = [];

    if (blocks && Array.isArray(blocks)) {
      blocks.forEach((block, idx) => {
        // Special handling for "Name" attribute
        let attr;
        let attributeValue;
        
        if (attributeName === 'Name') {
          attributeValue = block.blockData.name || `Block ${idx + 1}`;
        } else if (attributeName === 'Länge' || attributeName === 'Länge überschrieben') {
          // SPECIAL: Länge attributes are extracted from dimensions dynamically!
          // Check if block has line elements (pipe/line components)
          const hasLines = block.blockData.elements.some((el: any) => el.type === 'line');
          if (hasLines) {
            // Extract from dimensions
            const dimensions = block.blockData.elements.filter((el: any) => el.type === 'dimension');
            if (dimensions.length > 0) {
              const dimension = dimensions[0] as any;
              if (attributeName === 'Länge') {
                attributeValue = dimension.value ? parseFloat(dimension.value) : null;
              } else if (attributeName === 'Länge überschrieben') {
                const overridden = dimension.actualValue && dimension.actualValue !== dimension.value
                  ? parseFloat(dimension.actualValue)
                  : null;
                attributeValue = overridden;
              }
            }
          }
          // Mark as "found" for line components
          attr = hasLines ? { name: attributeName, value: attributeValue } : undefined;
        } else {
          attr = block.blockData.attributes?.find(a => a.name === attributeName);
          attributeValue = attr?.value;
        }

        // Only add if attribute exists or is "Name"
        if (attr || attributeName === 'Name') {
          const allAttrs = new Map<string, any>();
          block.blockData.attributes?.forEach(a => allAttrs.set(a.name, a.value));
          allAttrs.set('Name', block.blockData.name || `Block ${idx + 1}`);
          
          // CRITICAL: Add length attributes from dimensions for ALL line components!
          const hasLines = block.blockData.elements.some((el: any) => el.type === 'line');
          if (hasLines) {
            const dimensions = block.blockData.elements.filter((el: any) => el.type === 'dimension');
            if (dimensions.length > 0) {
              const dimension = dimensions[0] as any;
              
              // Calculated length
              const calculated = dimension.value ? parseFloat(dimension.value) : null;
              if (calculated !== null && !isNaN(calculated)) {
                allAttrs.set('Länge', calculated);
              }
              
              // Overridden length
              const overridden = dimension.actualValue && dimension.actualValue !== dimension.value
                ? parseFloat(dimension.actualValue)
                : null;
              if (overridden !== null && !isNaN(overridden)) {
                allAttrs.set('Länge überschrieben', overridden);
              }
            }
          }
          
          objectsWithAttribute.push({
            id: block.id,
            name: block.blockData.name || `Block ${idx + 1}`,
            type: 'block',
            attributeValue: attributeValue,
            allAttributes: allAttrs,
          });
        }
      });
    }

    if (connections && Array.isArray(connections)) {
      connections.forEach((conn, idx) => {
        const attr = conn.attributes?.find(a => a.name === attributeName);
        if (attr) {
          const allAttrs = new Map<string, any>();
          conn.attributes?.forEach(a => allAttrs.set(a.name, a.value));
          allAttrs.set('Name', `Verbindung ${idx + 1}`);
          
          objectsWithAttribute.push({
            id: conn.id,
            name: `Verbindung ${idx + 1}`,
            type: 'connection',
            attributeValue: attr.value,
            allAttributes: allAttrs,
          });
        }
      });
    }

    // Remove old data rows (keep header)
    newConfig.rows = newConfig.rows.filter(r => r.isHeader);

    // Create rows for each object
    objectsWithAttribute.forEach((obj, idx) => {
      const rowId = `row-${obj.type}-${obj.id}`;
      const rowIndex = idx + 1;

      const row: TableRow = {
        id: rowId,
        index: rowIndex,
        height: 40,
        data: {},
      };

      if (obj.type === 'block') {
        (row as any).blockId = obj.id;
      } else {
        (row as any).connectionId = obj.id;
      }

      // Store all attributes in row metadata for later use
      (row as any).allAttributes = obj.allAttributes;

      // Create cell for the new column
      row.data[newColumn.id] = {
        id: `cell-${rowId}-${newColumn.id}`,
        rowIndex,
        colIndex: columnIndex,
        value: obj.attributeValue,
        dataType: 'string',
        attributeRef: attributeName,
        isReadOnly: false,
        style: defaultCellStyle,
      };

      newConfig.rows.push(row);
    });
  } else {
    // Add cells for existing rows
    newConfig.rows.forEach(row => {
      if (row.isHeader) {
        // Add header cell
        row.data[newColumn.id] = {
          id: `cell-${row.id}-${newColumn.id}`,
          rowIndex: row.index,
          colIndex: columnIndex,
          value: attributeName,
          dataType: 'string',
          isHeader: true,
          isReadOnly: true,
          style: headerCellStyle,
        };
      } else {
        // Add data cell - get value from row's allAttributes
        const allAttrs = (row as any).allAttributes;
        console.log('🔍 Adding column to existing row:', {
          rowId: row.id,
          attributeName,
          allAttrs: allAttrs,
          allAttrsType: allAttrs ? (allAttrs instanceof Map ? 'Map' : 'Object') : 'null',
          allAttrsKeys: allAttrs instanceof Map ? Array.from(allAttrs.keys()) : Object.keys(allAttrs || {}),
        });
        
        // Handle both Map (fresh data) and Object (from localStorage)
        const value = allAttrs instanceof Map 
          ? (allAttrs.get(attributeName) || '') 
          : (allAttrs?.[attributeName] || '');
        
        console.log('📊 Value for attribute:', { attributeName, value });

        row.data[newColumn.id] = {
          id: `cell-${row.id}-${newColumn.id}`,
          rowIndex: row.index,
          colIndex: columnIndex,
          value,
          dataType: 'string',
          attributeRef: attributeName,
          isReadOnly: false,
          style: defaultCellStyle,
        };
      }
    });
  }

  // Update header cells
  const headerRow = newConfig.rows.find(r => r.isHeader);
  if (headerRow) {
    headerRow.data[newColumn.id] = {
      id: `cell-header-${newColumn.id}`,
      rowIndex: 0,
      colIndex: columnIndex,
      value: attributeName,
      dataType: 'string',
      isHeader: true,
      isReadOnly: true,
      style: headerCellStyle,
    };
  }

  return newConfig;
}

// Remove column
export function removeColumn(config: TableConfig, columnId: string): TableConfig {
  const newConfig = { ...config };
  
  // Remove column
  newConfig.columns = newConfig.columns.filter(col => col.id !== columnId);
  
  // Update column indices
  newConfig.columns.forEach((col, idx) => {
    col.index = idx;
  });

  // Remove cells from all rows
  newConfig.rows.forEach(row => {
    delete row.data[columnId];
  });

  return newConfig;
}

// Calculate cell position considering merges
export function getCellRenderInfo(
  cell: TableCell,
  config: TableConfig
): { shouldRender: boolean; width: number; height: number } {
  // Check if this cell is merged into another cell
  if (cell.merge?.isMergedInto) {
    return { shouldRender: false, width: 0, height: 0 };
  }

  // If no merge info, use default sizing
  if (!cell.merge || (!cell.merge.rowSpan && !cell.merge.colSpan)) {
    const column = config.columns.find(c => c.index === cell.colIndex);
    const row = config.rows.find(r => r.index === cell.rowIndex);
    return {
      shouldRender: true,
      width: column?.width || 100,
      height: row?.height || 40,
    };
  }

  // Calculate total width and height for merged cell
  const rowSpan = cell.merge.rowSpan || 1;
  const colSpan = cell.merge.colSpan || 1;
  
  let totalWidth = 0;
  let totalHeight = 0;

  // Calculate width across columns
  for (let c = 0; c < colSpan; c++) {
    const column = config.columns.find(col => col.index === cell.colIndex + c);
    totalWidth += column?.width || 100;
  }

  // Calculate height across rows
  for (let r = 0; r < rowSpan; r++) {
    const row = config.rows.find(row => row.index === cell.rowIndex + r);
    totalHeight += row?.height || 40;
  }

  return {
    shouldRender: true,
    width: totalWidth,
    height: totalHeight,
  };
}

// Add attribute to specific objects and update table
export function addAttributeToObjects(
  config: TableConfig,
  attributeConfig: import('@/app/components/table/AddAttributeDialog').AddAttributeConfig,
  blocks: PlacedBlock[],
  connections: SocketConnection[]
): {
  updatedBlocks: PlacedBlock[];
  updatedConnections: SocketConnection[];
} {
  console.log('🏗️ addAttributeToObjects called:', {
    name: attributeConfig.name,
    fillMode: attributeConfig.fillMode,
    targetObjects: attributeConfig.targetObjects,
    blocksCount: blocks.length,
    connectionsCount: connections.length,
  });
  
  let updatedBlocks = [...blocks];
  let updatedConnections = [...connections];
  let objectCounter = 0;

  // Add attribute to blocks if selected
  if (attributeConfig.targetObjects.blocks) {
    console.log('✅ Adding to blocks...');
    updatedBlocks = blocks.map((block, idx) => {
      // Check if we should add to this block
      const shouldAdd = attributeConfig.targetObjects.allBlockTypes ||
        block.blockData.name === attributeConfig.targetObjects.specificBlockType;

      console.log(`  Block ${idx} (${block.blockData.name}): shouldAdd = ${shouldAdd}`);
      
      if (!shouldAdd) return block;

      const newAttr: import('@/app/types/cad').PartAttribute = {
        id: `attr-${Date.now()}-${Math.random()}`,
        name: attributeConfig.name,
        type: attributeConfig.type,
        value: getAttributeValue(attributeConfig, objectCounter++),
      };

      console.log(`    ✅ Adding attribute:`, newAttr);

      return {
        ...block,
        blockData: {
          ...block.blockData,
          attributes: [...(block.blockData.attributes || []), newAttr],
        },
      };
    });
    console.log(`✅ Updated ${objectCounter} blocks`);
  } else {
    console.log('❌ Skipping blocks (not selected)');
  }

  // Add attribute to connections if selected
  if (attributeConfig.targetObjects.connections) {
    console.log('✅ Adding to connections...');
    updatedConnections = connections.map((conn, idx) => {
      const newAttr: import('@/app/types/cad').PartAttribute = {
        id: `attr-${Date.now()}-${Math.random()}`,
        name: attributeConfig.name,
        type: attributeConfig.type,
        value: getAttributeValue(attributeConfig, objectCounter++),
      };

      console.log(`  Connection ${idx}:`, newAttr);

      return {
        ...conn,
        attributes: [...(conn.attributes || []), newAttr],
      };
    });
    console.log(`✅ Updated ${objectCounter} connections`);
  } else {
    console.log('❌ Skipping connections (not selected)');
  }

  return {
    updatedBlocks,
    updatedConnections,
  };
}

// Helper function to get attribute value based on fill mode
function getAttributeValue(
  config: import('@/app/components/table/AddAttributeDialog').AddAttributeConfig,
  index: number
): string | number | boolean {
  console.log('🔢 getAttributeValue:', { fillMode: config.fillMode, index, config });
  
  switch (config.fillMode) {
    case 'empty':
      return '';
    case 'same':
      return config.fillValue || '';
    case 'numbering': {
      const num = (config.startNumber || 1) + index;
      const value = `${config.prefix || ''}${num}${config.suffix || ''}`;
      console.log('✅ Numbering value:', value);
      // CRITICAL: Return as STRING, not number!
      return String(value);
    }
    case 'custom':
      return '';
    default:
      return '';
  }
}